import { defineComponent, ref, onUnmounted, computed, watch } from 'vue';
import __vue_component__$1 from './Loading/Loading.vue.js';
import __vue_component__ from './Skeleton.vue.js';

var script = defineComponent({
    name: 'ImageComponent',
    components: {
        Skeleton: __vue_component__,
        Loading: __vue_component__$1,
    },
    props: {
        background: {
            type: String,
            default: 'default',
        },
        height: {
            type: Number,
            default: null,
        },
        hideSkeleton: Boolean,
        isLoading: Boolean,
        objectFit: {
            type: String,
            default: 'cover',
        },
        src: {
            type: String,
            default: null,
        },
        useLoading: Boolean,
        width: {
            type: Number,
            default: null,
        },
    },
    setup(props, { emit }) {
        const imageIsLoading = ref(true);
        const isError = ref(false);
        const transitionTimeElapsed = ref(false);
        let transitionedTimeout;
        const setLoading = (loading, error) => {
            if (!loading) {
                transitionedTimeout = setTimeout(() => {
                    transitionTimeElapsed.value = true;
                }, 400);
            }
            imageIsLoading.value = loading;
            isError.value = error;
            if (error)
                emit('error');
        };
        onUnmounted(() => {
            if (transitionedTimeout)
                clearTimeout(transitionedTimeout);
        });
        const computedIsLoading = computed(() => {
            if (props.isLoading)
                return true;
            return imageIsLoading.value;
        });
        /**
         * Keep the skeleton or loading animation around briefly so it can fade out
         * after the image is loaded in. This way, the background of the component
         * transitions from grey to white gracefully.
         */
        const renderLoader = computed(() => {
            if (props.isLoading || !transitionTimeElapsed.value)
                return true;
            return imageIsLoading.value;
        });
        const classNames = computed(() => {
            const names = [];
            if (props.background === 'transparent')
                names.push('ImageComponent--transparent');
            if (!computedIsLoading.value && !isError.value)
                names.push('ImageComponent--loaded');
            return names;
        });
        const imageClasses = computed(() => {
            const names = [];
            switch (props.objectFit) {
                case 'cover':
                    names.push('ImageComponent__img--cover');
                    break;
                case 'contain':
                    names.push('ImageComponent__img--contain');
            }
            if (isError.value || computedIsLoading.value) {
                names.push('ImageComponent__img--hidden');
            }
            return names;
        });
        watch(() => props.src, () => {
            if (!props.src)
                return;
            const image = new Image();
            image.src = props.src;
            image.onload = () => setLoading(false, false);
            image.onerror = () => setLoading(false, true);
        }, { immediate: true });
        return {
            classNames,
            computedIsLoading,
            isError,
            imageClasses,
            renderLoader,
        };
    },
});

export { script as default };
